// The facilities of a product as the points of its map. Pure functions: the
// map draws what they return.

import { RelationRow, linkRole, rolesOf } from "./productRelations";

// How exactly a point is placed on the map: a place, or the middle of the
// city or the state it was found in
export type PointPrecision = "exact" | "city" | "state";

// What the product does at a facility, as the map colours it
export type FacilityRole = "production" | "test" | "deployment" | "other";

// The roles in their fixed order, which is also the order a facility of
// several roles is coloured by: where a product is made says the most
export const FACILITY_ROLES: FacilityRole[] = ["production", "test", "deployment", "other"];

// The role of each relation the map tells apart, by its label
const ROLE_OF_LABEL: Record<string, FacilityRole> = {
  [linkRole("produced_at")]: "production",
  [linkRole("tested_at")]: "test",
  [linkRole("deployed_at")]: "deployment",
};

// How each role reads in the legend
export const FACILITY_ROLE_WORDS: Record<FacilityRole, string> = {
  production: "production",
  test: "testing",
  deployment: "deployment",
  other: "other",
};

// The first of the roles a facility plays, in the fixed order
const leadingRole = (labels: string[]): FacilityRole => {
  const roles = labels.map((label) => ROLE_OF_LABEL[label] ?? "other");
  return FACILITY_ROLES.find((role) => roles.includes(role)) ?? "other";
};

export interface MapFacility {
  row: RelationRow;
  // Every role the facility plays, where it is linked twice
  roles: string[];
  // The role the map colours it by
  role: FacilityRole;
}

export interface FacilityPoint extends MapFacility {
  latitude: number;
  longitude: number;
  precision: PointPrecision;
}

// The facilities known only to one country, which the map shades whole
export interface CountryFacilities {
  // Its ISO alpha-2 code
  country: string;
  facilities: MapFacility[];
  // The role its facilities share; null where they play different ones
  role: FacilityRole | null;
}

export interface FacilityMapModel {
  // The facilities with a place on the map, the roughest placed first, so
  // that the exact ones are drawn over the halos of the others
  points: FacilityPoint[];
  // The facilities known only to their country, by country, the most first
  countryOnly: CountryFacilities[];
  // The facilities with no location at all
  unplaced: number;
  // The roles of the facilities on the map, in the fixed order
  roles: FacilityRole[];
}

const PRECISION_ORDER: PointPrecision[] = ["state", "city", "exact"];

/**
 * The map of a product's facilities; null when none has a place on it,
 * nor a country.
 *
 * A facility linked under two relations is one point with two roles. One
 * placed in the middle of its country gets no point, which would claim a
 * place it does not know: its country is shaded instead, as is the country
 * of one never placed at all. One with coordinates but no tier is taken as
 * placed only roughly, as a state's middle.
 */
export const facilityMapModel = (rows: RelationRow[]): FacilityMapModel | null => {
  const entries = new Map<string, { row: RelationRow; roles: string[] }>();
  for (const row of rows) {
    const entry = entries.get(row.entity.id);
    if (entry) {
      entry.roles = Array.from(new Set([...entry.roles, ...rolesOf(row)]));
    } else {
      entries.set(row.entity.id, { row, roles: rolesOf(row) });
    }
  }

  const points: FacilityPoint[] = [];
  const byCountry = new Map<string, MapFacility[]>();
  let unplaced = 0;
  for (const { row, roles: labels } of Array.from(entries.values())) {
    const played = { roles: labels, role: leadingRole(labels) };
    const { latitude, longitude, locationPrecision } = row;
    const country = row.countries?.[0];
    if (latitude == null || longitude == null || locationPrecision === "country") {
      // Never placed, or placed only in the middle of its country: the
      // country is what is known, if it is named
      if (country) {
        byCountry.set(country, [...(byCountry.get(country) ?? []), { row, ...played }]);
      } else {
        unplaced += 1;
      }
    } else {
      points.push({
        row,
        ...played,
        latitude,
        longitude,
        precision: locationPrecision ?? "state",
      });
    }
  }

  if (points.length === 0 && byCountry.size === 0) {
    return null;
  }
  const countryOnly = Array.from(byCountry, ([country, facilities]) => {
    const shared = new Set(facilities.map((facility) => facility.role));
    return {
      country,
      facilities,
      role: shared.size === 1 ? facilities[0].role : null,
    };
  }).sort(
    (left, right) =>
      right.facilities.length - left.facilities.length ||
      left.country.localeCompare(right.country),
  );
  const present = new Set(
    [...points, ...countryOnly.flatMap((entry) => entry.facilities)].map((facility) => facility.role),
  );
  return {
    points: points.sort(
      (left, right) =>
        PRECISION_ORDER.indexOf(left.precision) - PRECISION_ORDER.indexOf(right.precision),
    ),
    countryOnly,
    unplaced,
    roles: FACILITY_ROLES.filter((role) => present.has(role)),
  };
};

// How a place reads in its tooltip
export const PRECISION_WORDS: Record<PointPrecision | "country", string> = {
  exact: "exact location",
  city: "around the city it is in",
  state: "around the region it is in",
  country: "only its country is known",
};
